/**
 * Recovery Loop reads — the live case list + one conversation's message thread.
 *
 * A "case" is an eligibility check the SMS recovery sweep acted on
 * (`inRecoveryLoop == true`). The check carries the display identity
 * (name/DOB/reason/payer) natively; its OWN case doc
 * (`smsConversations/{phone}/cases/{checkId}`) carries the FINE lifecycle
 * state + the card extraction — per check, so a phone with several concurrent
 * cases (grouped siblings) shows each with its own state, never another
 * case's. Both layers are live: one listener on the check cohort plus one
 * per-case case-doc listener, joined client-side — no denormalization, no
 * manual refresh.
 *
 * Non-admin reads MUST include a `clinicId ==` constraint (Firestore rules can't
 * prove the clinic match without it — see services/eligibilityChecks.ts).
 * Everything here is read-only; writes (Mark handled) go through callables.
 */
import {
  onSnapshot, query, where, orderBy, limit,
  type QueryConstraint,
} from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { functions } from '../config'
import {
  orgEligibilityChecksCollection,
  smsCaseRef,
  smsConversationRef,
  smsMessagesCollection,
} from './firestore'
import type { CardExtraction } from '../../shared/schemas/cardExtraction'
import type { CorrectableCheck } from '../utils/checkCorrection'

/**
 * The follow-up lifecycle state a case renders at. Mirrors the conversation's
 * `followupState` plus the check-only coarse outcomes (`no_phone`,
 * `sms_failed`). The `*_rerun` / `resolved` / `extracted_same` values are
 * defined for the future step-6 re-check. A manual "Mark handled" close is
 * NOT a state — it's the separate `handled` flag, so a closed case keeps its
 * full journey on the stepper while chip/bucket read Resolved via the flag.
 */
export type RecoveryDisplayState =
  | 'sms_sent'
  | 'needs_clarification'
  | 'awaiting_reply'
  | 'extracted'
  | 'cannot_extract'
  | 'opted_out'
  | 'no_phone'
  | 'sms_failed'
  | 'check_rerun'
  | 'resolved'
  | 'rerun_failed'
  | 'extracted_same'
  /** A human is needed (#304) — the automation has stopped. */
  | 'escalated'

/** The conversation's aggregated card read (mirror of the backend shape). */
export interface RecoveryExtraction {
  status: 'extracted' | 'needs_clarification' | 'cannot_extract'
  data?: CardExtraction
  /** Per-field cross-read agreement (0–1) from the consensus read. Absent on
   *  error paths and pre-consensus docs. Legacy badge input: on docs with no
   *  stored `payerDiffers` verdict the drawer falls back to the payer score +
   *  string comparison (#267). */
  confidence?: Partial<Record<keyof CardExtraction, number>>
  /** The #267 different-insurer verdict the extraction fold DECIDED (string
   *  prefilter + LLM equivalence gate) — the same value the re-run applies
   *  (`resolvePayerDiffers`), so the badge can't claim a swap that won't
   *  happen. Three-state: true/false = decided, null = unconfirmed (gate
   *  unavailable — no swap, re-asked), absent = pre-gate doc (fall back to the
   *  string comparison). */
  payerDiffers?: boolean | null
  neededFields: string[]
  missingFields: string[]
  photoCount: number
  error?: string
  parsedAt?: string
}

export interface RecoveryCase {
  checkId: string
  clinicId: string | null
  /**
   * The correction chain the case's check belongs to (#349): `flowId`, then
   * the dedup-set id, then the doc id — the same fallbacks the rest of the app
   * derives a flow key with, so a chain minted before flows existed still has
   * one. The drawer reads the chain's NEWEST check by it, which is how it
   * sees successors the sweeps mint without a `supersededBy` pointer.
   */
  flowId: string | null
  phone: string | null
  firstName: string
  lastName: string
  dob: string
  /**
   * What the check actually ASKED the payer — the drawer's correction editor
   * edits exactly this, and it is not always the record above.
   *
   * `identity` is the set identity in whatever shape the source supplied it
   * (Revolution and CSV write `"06/02/1979"`); `requestSubscriber` is the audit
   * of what was sent. Sourcing the editor from the identity is how a
   * date-of-birth-mismatch case opened with the date of birth blank (#322) —
   * the one field it exists to correct. Every field the editor shows comes
   * through here, so the drawer and the check panels cannot disagree about what
   * the check said.
   */
  submitted: {
    firstName: string
    lastName: string
    dateOfBirth: string
    memberId: string
  }
  /** The check's own status; `pending` means there is no answer to correct yet. */
  checkStatus: string | null
  payerName: string
  /** The RAW payer shorthand the failed check requested, before resolution —
   *  what a staff payer correction must key its per-clinic alias on (#267).
   *  Null on pre-#230 docs that didn't persist it; callers fall back to
   *  `payerName`. */
  requestPayerName: string | null
  rejectionCategory: string | null
  /** Human-readable failure reason for the row subtitle. */
  reasonLabel: string
  appointmentDate: string | null
  checkedAt: string | null
  /** = patient phone; the conversation doc id. Null for no_phone cases. */
  conversationId: string | null
  displayState: RecoveryDisplayState
  handled: boolean
  /** When a staff member closed the case by hand — dates the "closed by staff"
   *  band, and distinguishes a human giving up from a loop recovery. */
  handledAt: string | null
  /** Newest of the conversation's activity / the check time — for sorting. */
  lastActivity: string | null
  correctionFields: string[] | null
  /** Why a rerun_failed case parked (#269): "rounds_exhausted"/"repeat_fields"
   *  mean real progress before the stop — the band words differ. */
  rerunFailureReason: string | null
  /** The check this case's re-run FIRED (#279) — what the drawer's "View
   *  check" link opens once a re-run exists; null until one fires (and on
   *  legacy cases resolved before the linkage was stamped on all outcomes). */
  rerunCheckId: string | null
  /** When a card answering a SIBLING case landed on this thread (#312) — the
   *  band and the Extraction pane say "we asked for their other card" instead
   *  of describing a read that never happened for this case. */
  siblingCardArrivedAt: string | null
  /** #312 — a photo on this thread is awaiting a "whose card?" answer. */
  unattributedCardAt: string | null
  otherPatientCardAt: string | null
  extraction: RecoveryExtraction | null
  /**
   * Fields the patient answered in WRITING, not off the card. The agent turn
   * merges a texted answer into the same read as the OCR (found + confidence
   * 1), so the extraction alone cannot say where a value came from — this is
   * the backend's own durable record of it, and a whole-set photo rebuild
   * re-applies these on top precisely so a later photo can't wipe them. Key
   * present ⇒ the value now shown for that field is the patient's statement.
   * Null on cases with no texted answers and on pre-#327 docs.
   */
  statedFields: Partial<Record<StatedField, string>> | null
}

/** The card fields a patient can answer in writing (mirror of the backend's
 *  CardExtractionField). */
export type StatedField =
  | 'memberId' | 'firstName' | 'lastName' | 'dateOfBirth' | 'payerName'

const STATED_FIELDS: StatedField[] =
  ['memberId', 'firstName', 'lastName', 'dateOfBirth', 'payerName']

/** Reads the case doc's `statedFields`, keeping only known non-empty entries —
 *  an unknown key from a newer backend must not render as a card row. */
function toStatedFields(raw: unknown): Partial<Record<StatedField, string>> | null {
  if (!raw || typeof raw !== 'object') return null
  const src = raw as Record<string, unknown>
  const out: Partial<Record<StatedField, string>> = {}
  let any = false
  for (const f of STATED_FIELDS) {
    const v = src[f]
    if (typeof v === 'string' && v.trim()) { out[f] = v; any = true }
  }
  return any ? out : null
}

/**
 * The case as the correction editor sees it (#322).
 *
 * ONE mapping, exported so the drawer and its test read the same code: the
 * ticket's requirement is that this editor opens with the SAME five values as
 * the check panels, and a test that re-states the mapping inline would go on
 * passing the day the drawer's copy drifts.
 */
export function correctableFromCase(rc: RecoveryCase): CorrectableCheck {
  return {
    id: rc.checkId,
    clinicId: rc.clinicId,
    status: rc.checkStatus as CorrectableCheck['status'],
    payerName: rc.payerName,
    requestPayerName: rc.requestPayerName,
    // What the check SENT, not the record the drawer prints above it — the two
    // differ in shape, and the editor must show the question that was asked.
    requestSubscriber: rc.submitted,
  }
}

export interface RecoveryMessage {
  id: string
  direction: 'inbound' | 'outbound'
  type: 'SMS' | 'MMS'
  text: string
  status: string
  media: Array<{ intakeId: string; objectIndex: number; contentType: string }>
  occurredAt: string | null
  createdAt: string | null
  /** Carrier/Telnyx failure code on a send that didn't land (e.g. "30005") —
   *  the "why" behind a red delivery-failure band. Null on healthy messages. */
  errorCode: string | null
  /** Agent intent that composed an automated outbound — the "Suggested by
   *  VitalRev AI" marker (#269). Null on manual sends and every inbound. */
  aiIntent: string | null
  /** The case an inbound was attributed to (#279) — scopes the Extraction
   *  pane's photo to the case being viewed. Null while pending/parked, on
   *  outbounds, and on pre-attribution legacy docs. */
  attributedCaseId: string | null
}

/**
 * The thread-level nudge cadence, read straight off the conversation doc — what
 * the drawer needs to tell staff whether the automation is still chasing this
 * patient ("Nudge 2 of 3 at 11:30 AM") or has handed the case over.
 *
 * Thread-level by design: one reminder covers every open case on a phone, so
 * these values are shared by sibling cases and belong to the conversation, not
 * the check.
 */
export interface RecoveryNudge {
  /** ISO due time of the next reminder; NUDGE_HALTED = the cadence stopped. */
  nextNudgeAt: string | null
  /** Reminders already sent this episode (0 right after the first outreach). */
  nudgeCount: number
  /** ISO instant of the last text we sent — drives "Texted 4h ago". */
  lastOutboundAt: string | null
  /** The patient replied STOP — no further automated texts, ever. */
  optedOut: boolean
}

/** Map a Stedi rejection category to the row's failure-reason label. */
export function recoveryReasonLabel(category: string | null | undefined): string {
  switch (category) {
    case 'member_not_found': return 'Member ID mismatch'
    case 'invalid_dob': return 'Date of birth mismatch'
    case 'coverage_terminated': return 'Coverage terminated'
    case 'no_match_or_invalid_subscriber': return 'Payer or subscriber not found'
    default: return 'Needs correction'
  }
}

type RawDoc = Record<string, unknown>

function str(v: unknown): string {
  return typeof v === 'string' ? v : ''
}
function strOrNull(v: unknown): string | null {
  return typeof v === 'string' && v ? v : null
}
/** First source that actually holds text — a stored empty string is the absence
 *  of a value, not a value, and must not shadow the fallback behind it. */
function firstStr(...values: unknown[]): string {
  for (const v of values) {
    if (typeof v === 'string' && v.trim()) return v
  }
  return ''
}

/** Every RecoveryDisplayState — the compiler enforces the members, the runtime
 *  set guards the Firestore-string casts below: the stepper/bucket/hue
 *  switches are exhaustive over the union, so an UNKNOWN backend state (a
 *  future value shipped without a frontend update) must fall back here rather
 *  than return undefined and crash the Recovery Loop page. */
const KNOWN_DISPLAY_STATES: RecoveryDisplayState[] = [
  'sms_sent', 'needs_clarification', 'awaiting_reply', 'extracted',
  'cannot_extract', 'opted_out', 'no_phone', 'sms_failed', 'check_rerun',
  'resolved', 'rerun_failed', 'extracted_same', 'escalated',
]
const KNOWN_DISPLAY_STATE_SET: ReadonlySet<string> = new Set(KNOWN_DISPLAY_STATES)

/** Exported for tests only. */
export function deriveDisplayState(check: RawDoc, caseDoc: RawDoc | undefined): RecoveryDisplayState {
  // The check's OWN case is authoritative — a sibling's success on the same
  // phone can never render this case "Resolved".
  const fine = caseDoc?.followupState
  if (typeof fine === 'string' && KNOWN_DISPLAY_STATE_SET.has(fine)) {
    return fine as RecoveryDisplayState
  }
  // No case doc (no_phone / a send that failed before any case existed / an
  // unreadable doc) — or an unknown fine state: the check's coarse outreach
  // outcome is the display state, with sms_sent as the last-resort default.
  const coarse = str(check.smsOutreachState)
  return KNOWN_DISPLAY_STATE_SET.has(coarse)
    ? (coarse as RecoveryDisplayState)
    : 'sms_sent'
}

function toRecoveryCase(check: RawDoc, caseDoc: RawDoc | undefined): RecoveryCase {
  const identity = (check.identity ?? {}) as RawDoc
  const sub = check.requestSubscriber as RawDoc | undefined
  const rejectionCategory = strOrNull(check.rejectionCategory)
  const extraction = (caseDoc?.extraction ?? null) as RecoveryExtraction | null
  // Prefer the case's activity timestamp; fall back to the check time.
  const lastActivity =
    strOrNull(caseDoc?.updatedAt) ??
    strOrNull(check.checkedAt)
  return {
    checkId: str(check.id),
    clinicId: strOrNull(check.clinicId),
    flowId: firstStr(check.flowId, check.eligibilityCheckSetId, check.id) || null,
    phone: strOrNull(check.patientPhone) ?? strOrNull(check.conversationId),
    firstName: str(identity.firstName),
    lastName: str(identity.lastName),
    dob: str(identity.dob),
    // #313: what the check ASKED the payer, so the drawer can offer the same
    // in-place correction as the check panels. The stored identity is the
    // fallback for docs predating requestSubscriber, exactly as elsewhere.
    submitted: {
      firstName: firstStr(sub?.firstName, identity.firstName),
      lastName: firstStr(sub?.lastName, identity.lastName),
      dateOfBirth: firstStr(sub?.dateOfBirth, identity.dob),
      memberId: firstStr(sub?.memberId, identity.memberId, identity.policyNumber),
    },
    checkStatus: strOrNull(check.status),
    payerName: str(check.payerName),
    requestPayerName: strOrNull(check.requestPayerName),
    rejectionCategory,
    reasonLabel: recoveryReasonLabel(rejectionCategory),
    appointmentDate: strOrNull(check.appointmentDate),
    checkedAt: strOrNull(check.checkedAt),
    conversationId: strOrNull(check.conversationId),
    displayState: deriveDisplayState(check, caseDoc),
    handled: !!check.recoveryHandledAt,
    handledAt: strOrNull(check.recoveryHandledAt),
    lastActivity,
    correctionFields: Array.isArray(caseDoc?.correctionFields)
      ? (caseDoc!.correctionFields as string[])
      : null,
    rerunFailureReason: strOrNull(caseDoc?.rerunFailureReason),
    rerunCheckId: strOrNull(caseDoc?.rerunCheckId),
    siblingCardArrivedAt: strOrNull(caseDoc?.siblingCardArrivedAt),
    unattributedCardAt: strOrNull(caseDoc?.unattributedCardAt),
    otherPatientCardAt: strOrNull(caseDoc?.otherPatientCardAt),
    extraction,
    statedFields: toStatedFields(caseDoc?.statedFields),
  }
}

/**
 * Live view of the org's Recovery Loop cases, newest activity first. Pass
 * `clinicId` for non-admin callers (required by rules); omit for an admin
 * org-wide view. `checkedAfter` bounds the window on the check's `checkedAt`
 * (same server-side filter as Recent Checks); null = all time.
 *
 * One listener on the checks query plus one listener per check's own CASE doc
 * (`smsConversations/{phone}/cases/{checkId}`, attached/detached as checks
 * enter/leave the result set), so a card reply or extraction moves a row
 * without a reload. A case doc the caller can't read resolves absent — the row
 * renders its coarse check state, same contract as before. The first
 * `onChange` waits for every initial case doc to report (no coarse-state flash
 * on load); rows arriving later render immediately and upgrade when their case
 * loads. Returns an unsubscribe that tears the whole tree down.
 */
export function subscribeRecoveryCases(
  orgId: string,
  clinicId: string | null | undefined,
  checkedAfter: Date | null,
  onChange: (cases: RecoveryCase[]) => void,
  onError: (err: Error) => void,
  max = 100,
): () => void {
  const constraints: QueryConstraint[] = [where('inRecoveryLoop', '==', true)]
  if (clinicId) constraints.push(where('clinicId', '==', clinicId))
  if (checkedAfter) constraints.push(where('checkedAt', '>=', checkedAfter.toISOString()))
  constraints.push(orderBy('checkedAt', 'desc'), limit(max))

  let checks: RawDoc[] = []
  let checksArrived = false
  // Case docs keyed by `${phone}|${checkId}` (the doc path minus the org).
  const caseDocs = new Map<string, RawDoc>()
  const caseUnsubs = new Map<string, () => void>()
  // Keys from the FIRST checks snapshot whose case doc hasn't reported.
  const initialPending = new Set<string>()

  const caseKey = (c: RawDoc): string | null => {
    const phone = strOrNull(c.conversationId)
    return phone ? `${phone}|${str(c.id)}` : null
  }

  const emit = () => {
    if (!checksArrived || initialPending.size > 0) return
    const cases = checks.map((c) =>
      toRecoveryCase(c, caseDocs.get(caseKey(c) ?? '')),
    )
    // Order by real activity (a card reply/extraction is newer than the check).
    onChange(cases.sort((a, b) => (b.lastActivity ?? '').localeCompare(a.lastActivity ?? '')))
  }

  const watchCase = (key: string) => {
    const [phone, checkId] = key.split('|')
    caseUnsubs.set(key, onSnapshot(
      smsCaseRef(orgId, phone, checkId),
      (snap) => {
        if (snap.exists()) caseDocs.set(key, snap.data() as RawDoc)
        else caseDocs.delete(key)
        initialPending.delete(key)
        emit()
      },
      () => {
        // Unreadable (e.g. null-clinic doc for a non-admin) → coarse state.
        caseDocs.delete(key)
        initialPending.delete(key)
        emit()
      },
    ))
  }

  const checksUnsub = onSnapshot(
    query(orgEligibilityChecksCollection(orgId), ...constraints),
    (snap) => {
      const first = !checksArrived
      checks = snap.docs.map((d) => ({ id: d.id, ...(d.data() as RawDoc) }))
      const wanted = new Set(
        checks.map(caseKey).filter((k): k is string => !!k),
      )
      for (const [key, unsub] of caseUnsubs) {
        if (!wanted.has(key)) {
          unsub()
          caseUnsubs.delete(key)
          caseDocs.delete(key)
          initialPending.delete(key)
        }
      }
      for (const key of wanted) {
        if (!caseUnsubs.has(key)) {
          if (first) initialPending.add(key)
          watchCase(key)
        }
      }
      checksArrived = true
      emit()
    },
    (err) => onError(err instanceof Error ? err : new Error(String(err))),
  )

  return () => {
    checksUnsub()
    for (const unsub of caseUnsubs.values()) unsub()
    caseUnsubs.clear()
  }
}

/**
 * Live view of ONE conversation's nudge cadence — subscribed while the drawer is
 * open, torn down on close.
 *
 * Deliberately drawer-only: the cadence is what the status band explains
 * ("Nudge 2 of 3 at 11:30 AM"), and the case rows don't show it — so a 100-row
 * list costs zero extra listeners. An unreadable doc (permissions, or a thread
 * that hasn't been created yet) resolves to null, and the band falls back to
 * copy that doesn't claim a nudge is coming.
 */
export function subscribeRecoveryNudge(
  orgId: string,
  phone: string,
  onChange: (nudge: RecoveryNudge | null) => void,
): () => void {
  return onSnapshot(
    smsConversationRef(orgId, phone),
    (snap) => {
      if (!snap.exists()) { onChange(null); return }
      const d = snap.data() as RawDoc
      onChange({
        nextNudgeAt: strOrNull(d.nextNudgeAt),
        nudgeCount: typeof d.nudgeCount === 'number' ? d.nudgeCount : 0,
        lastOutboundAt: strOrNull(d.lastOutboundAt),
        optedOut: d.optedOut === true,
      })
    },
    () => onChange(null),
  )
}

/**
 * Live view of one conversation's messages, oldest first (the slideout thread —
 * subscribed while the drawer is open, torn down on close). Pass the case's
 * `clinicId` so a non-admin `list` query is provably clinic-scoped (rules
 * require the constraint). Sorted client-side to avoid a composite index — a
 * conversation carries only a handful of messages.
 */
export function subscribeRecoveryMessages(
  orgId: string,
  phone: string,
  clinicId: string | null | undefined,
  onChange: (messages: RecoveryMessage[]) => void,
  onError: (err: Error) => void,
): () => void {
  const constraints: QueryConstraint[] = []
  if (clinicId) constraints.push(where('clinicId', '==', clinicId))
  return onSnapshot(query(smsMessagesCollection(orgId, phone), ...constraints), (snap) => {
    const msgs = snap.docs.map((d) => {
      const m = d.data() as RawDoc
      const media = Array.isArray(m.media)
        ? (m.media as RawDoc[]).map((x) => ({
          intakeId: str(x.intakeId),
          objectIndex: typeof x.objectIndex === 'number' ? x.objectIndex : 0,
          contentType: str(x.contentType),
        }))
        : []
      return {
        id: d.id,
        direction: m.direction === 'inbound' ? 'inbound' : 'outbound',
        type: m.type === 'MMS' ? 'MMS' : 'SMS',
        text: str(m.text),
        status: str(m.status),
        media,
        occurredAt: strOrNull(m.occurredAt),
        createdAt: strOrNull(m.createdAt),
        errorCode: strOrNull(m.errorCode),
        aiIntent: strOrNull(m.agentIntent),
        attributedCaseId: strOrNull(m.attributedCaseId),
      } as RecoveryMessage
    })
    onChange(msgs.sort((a, b) => (a.createdAt ?? '').localeCompare(b.createdAt ?? '')))
  }, (err) => onError(err instanceof Error ? err : new Error(String(err))))
}

/** A card image from the recovery thread, read through the audited callable (bytes inline, base64). */
export async function getRecoveryCardImage(input: {
  orgId: string
  phone: string
  intakeId: string
  objectIndex: number
}): Promise<{ data: string; contentType: string }> {
  const fn = httpsCallable<typeof input, { data: string; contentType: string }>(functions, 'getRecoveryCardImage')
  const res = await fn(input)
  return res.data
}

/** Closes a recovery case by hand ("Mark handled"). */
export async function markRecoveryHandled(orgId: string, checkId: string): Promise<void> {
  const fn = httpsCallable<{ orgId: string; checkId: string }, { ok: boolean }>(functions, 'markRecoveryHandled')
  await fn({ orgId, checkId })
}

/** Puts an escalated case back in the automated flow (#304). */
export async function returnRecoveryToFlow(orgId: string, checkId: string): Promise<void> {
  const fn = httpsCallable<{ orgId: string; checkId: string }, { ok: boolean }>(functions, 'returnRecoveryToFlow')
  await fn({ orgId, checkId })
}

export interface SendRecoverySmsResult {
  sent: boolean
  /** Why nothing was sent: `quiet_hours` (confirm and retry), `opted_out`, `no_phone`, … */
  reason?: string
  /** The patient's local hour, with `quiet_hours`. */
  localHour?: number
}

/**
 * Staff text to a recovery case's patient (`sendSms`). The backend resolves the
 * phone and clinic from the check — the client never sends a phone number.
 * `confirmAfterHours` sends inside quiet hours after staff confirmed.
 */
export async function sendRecoverySms(input: {
  orgId: string
  checkId: string
  text: string
  confirmAfterHours?: boolean
}): Promise<SendRecoverySmsResult> {
  const fn = httpsCallable<typeof input, SendRecoverySmsResult>(functions, 'sendSms')
  const res = await fn(input)
  return res.data
}
